import { useState, type FC } from 'react';
import { Button } from '@carbon/react';
import { SidePanelClose, SidePanelOpen } from '@carbon/react/icons';
import { ActivityFeedPanelOpenButton } from 'components/activity-feed-panel';
import { SuccessToast } from 'components/success-toast';
import ErrorToast from 'components/error-toast';
import { AreaDetailBody } from 'pages/area-geometries/show/area-detail-body';
import { AreaGeometryEditor } from 'lib/area-geometries';
import type { AreaGeometry } from 'types/area-geometry';
import type { ActionNotice, AreaWorkspace } from 'types/area-workspace';
import { AreaWorkspaceHeader } from './area-workspace-header';
import { GeometrySavedToast } from './geometry-saved-toast';
import {
  useAreaVerdictControls,
  useGeometryDraft,
  useGeometryEditor,
} from './hooks';
import {
  AreaLifecyclePanel,
  type AreaLifecycleAction,
} from './area-lifecycle-panel';
import { AreaVerdictPanel } from './area-verdict-panel';
import { useAreaFlags } from './use-area-flags';
import { useWorkspacePromotion } from './use-workspace-writes';
import styles from './area-workspace-content.module.scss';

interface AreaWorkspaceContentProps {
  workspace: AreaWorkspace;
  area: AreaGeometry;
  /** Leaves the workspace for the area, carrying a confirmation when there is one. */
  onLeave: (areaId: number, notice?: ActionNotice) => void;
  // Refetches the area after an in-place edit — archiving and the map flag change it,
  // and the copy shown here has to keep saying what is true.
  onAreaChanged: () => void;
  /** Re-reads the workspace, which carries the verdict. */
  onWorkspaceChanged: () => void;
}

/**
 * The area's row under the workspace bar: its name, plus the rail toggle and activity
 * drawer the detail page carries in the same place.
 * @param {object} props The props.
 * @param {string} props.areaName The area's name.
 * @param {boolean} props.panelOpen Whether the settings rail is open.
 * @param {() => void} props.onTogglePanel Opens or closes the rail.
 * @return {JSX.Element} The row.
 */
function AreaBar({
  areaName,
  panelOpen,
  onTogglePanel,
}: {
  areaName: string;
  panelOpen: boolean;
  onTogglePanel: () => void;
}) {
  return (
    <div className={styles.areaBar}>
      <h2 className={styles.areaName}>{areaName}</h2>
      <div className={styles.areaActions}>
        <Button
          kind="tertiary"
          size="md"
          hasIconOnly
          iconDescription={
            panelOpen ? 'Hide settings panel' : 'Show settings panel'
          }
          tooltipPosition="bottom"
          renderIcon={panelOpen ? SidePanelClose : SidePanelOpen}
          onClick={onTogglePanel}
        />
        {/* Below and end-aligned: this bar runs full-bleed, so a centred tooltip's
            caret overflows the document and flashes a scrollbar on hover. */}
        <ActivityFeedPanelOpenButton
          tooltipPosition="bottom"
          tooltipAlignment="end"
        />
      </div>
    </div>
  );
}

// The area's detail content under the workspace bar. Everything that changes the area —
// promoting it, archiving it, taking it off the map — happens here, and each one warns
// about the records pointing at the area before it goes through.
export const AreaWorkspaceContent: FC<AreaWorkspaceContentProps> = ({
  workspace,
  area,
  onLeave,
  onAreaChanged,
  onWorkspaceChanged,
}) => {
  // The rail starts open here as it does on the detail page.
  const [panelOpen, setPanelOpen] = useState(true);
  // Which lifecycle panel is open, or null.
  const [pending, setPending] = useState<AreaLifecycleAction | null>(null);
  // What the last in-place edit did, for the toast that reports it.
  const [saved, setSaved] = useState<ActionNotice | null>(null);
  // Whether the failed flag write was the unarchive, which reports as a toast.
  const [unarchiveRefused, setUnarchiveRefused] = useState(false);
  // Edit Area Geometry (Epic 2, behind its flag).
  const geometryEditor = useGeometryEditor();
  const { shownArea, lastSave, recordSave, dismissSave } =
    useGeometryDraft(area);

  const areaName = area.displayName || area.name;
  // Promoting freezes the workspace: the backend refuses edits afterwards, so nothing
  // here offers them either. `promoted` is the signal that matters — promotion is
  // one-way for every type now, so the link reports `["create"]` unconditionally and
  // says nothing about this workspace. The auth read stays as a guard in case a type
  // ever withholds it.
  const frozen =
    workspace.promoted ||
    !workspace.resources.promotions.auth.includes('create');

  const promotion = useWorkspacePromotion(
    workspace.resources.promotions.href,
    () =>
      onLeave(area.id, {
        title: 'Promoted to map layers',
        subtitle: `${areaName} promoted as the current version of this area.`,
      })
  );

  const flags = useAreaFlags(workspace.detail.areaGeometryId);

  const verdict = useAreaVerdictControls(
    workspace.detail.markCorrect,
    onWorkspaceChanged
  );

  // A verdict being saved and a promote must not cross: the promote freezes the
  // workspace, and a verdict landing after it would be refused or lost.
  const writing = promotion.busy || flags.saving || verdict.panel.setter.saving;

  const closePanel = () => {
    setPending(null);
    promotion.reset();
    flags.reset();
  };

  const handleConfirm = async (reason: string) => {
    if (pending === 'promote') {
      promotion.promote(reason || undefined);
      return;
    }

    if (!(await flags.save({ active: false }, reason || undefined))) {
      // Refused: the panel stays open and says why.
      return;
    }
    // Archiving stays in the workspace, as the mock does: the panel closes, the toast
    // reports it, and the area re-reads so Active says No. Only promotion leaves.
    setPending(null);
    setSaved({
      title: 'Area archived',
      subtitle: `${areaName} archived.`,
    });
    onAreaChanged();
  };

  // The inverse, offered while the area is archived and the workspace unpromoted.
  // Restorative, so it saves straight away like the map flag — archiving is the
  // change that warns first. Its refusal gets a toast of its own: `flags.error` is
  // shared with the map flag, whose message renders beside the Shared checkbox,
  // and an unarchive refusal there would read as the wrong field failing.
  const handleUnarchive = async () => {
    setUnarchiveRefused(false);
    if (!(await flags.save({ active: true }))) {
      setUnarchiveRefused(true);
      return;
    }
    setSaved({
      title: 'Area unarchived',
      subtitle: `${areaName} is active again.`,
    });
    onAreaChanged();
  };

  // The map flag saves where it was edited, either way, and reports itself with a
  // toast — no panel, as the mock has it. Archiving is the change that warns first.
  const handleSharedChange = async (next: boolean) => {
    if (!(await flags.save({ shared: next }))) {
      return;
    }
    onAreaChanged();
    setSaved(
      next
        ? {
            title: 'Added to map layers',
            subtitle: `${areaName} published as a layer on Insights maps.`,
          }
        : {
            title: 'Removed from map layers',
            subtitle: `${areaName} removed from Insights map layers.`,
          }
    );
  };

  return (
    <div className={styles.shell}>
      <AreaWorkspaceHeader
        name={workspace.name}
        subtitle={areaName}
        promoted={frozen}
        active={area.active}
        busy={writing}
        onBack={() => onLeave(area.id)}
        onPromote={() => setPending('promote')}
        onArchive={() => setPending('archive')}
        onUnarchive={handleUnarchive}
        areaGeometryId={workspace.detail.areaGeometryId}
        {...verdict.header}
        verdictDisabled={verdict.header.verdictDisabled || writing}
      />

      <AreaBar
        areaName={areaName}
        panelOpen={panelOpen}
        onTogglePanel={() => setPanelOpen((open) => !open)}
      />

      {/* The detail body owns the rail, the cards and their scrolling. The map flag is
          editable here and nowhere else, and only while the workspace is unfrozen. */}
      <AreaDetailBody
        area={shownArea}
        panelOpen={panelOpen}
        onSharedChange={frozen ? undefined : handleSharedChange}
        sharedSaving={flags.saving}
        sharedError={pending || unarchiveRefused ? null : flags.error}
        onEditGeometry={frozen ? undefined : geometryEditor.open}
      />

      {!frozen && (
        <AreaGeometryEditor
          open={geometryEditor.editing}
          area={shownArea}
          workspace={workspace}
          upload={geometryEditor.upload}
          onClose={geometryEditor.close}
          onSaved={(saved) => {
            recordSave(saved);
            onAreaChanged();
          }}
        />
      )}

      {saved && (
        <SuccessToast
          message={saved.title}
          subtitle={saved.subtitle}
          onClose={() => {
            setSaved(null);
            return true;
          }}
        />
      )}

      {lastSave && (
        <GeometrySavedToast
          // A new save is a new toast, with its own counts and timer.
          key={lastSave.id}
          areaName={areaName}
          before={lastSave.before}
          after={lastSave.after}
          onClose={dismissSave}
        />
      )}

      {unarchiveRefused && flags.error && (
        <ErrorToast
          message="Could not unarchive the area"
          subtitle={flags.error}
          onClose={() => {
            setUnarchiveRefused(false);
            flags.reset();
            return true;
          }}
        />
      )}

      <AreaVerdictPanel areaName={areaName} {...verdict.panel} />

      {pending && (
        <AreaLifecyclePanel
          open
          action={pending}
          areaName={areaName}
          workspaceName={workspace.name}
          areaGeometryId={workspace.detail.areaGeometryId}
          businessId={area.businessId}
          shared={area.shared}
          busy={writing}
          error={pending === 'promote' ? promotion.error : flags.error}
          onClose={closePanel}
          onConfirm={handleConfirm}
        />
      )}
    </div>
  );
};
